import { Button } from '@liveauctioneers/hammer-ui-core/button';
import { FormattedMessage, useIntl } from '@liveauctioneers/hammer-ui-core/intl';
import { Link } from '@liveauctioneers/hammer-ui-core/link';
import { BodyPrimary, BodySecondary, H2, H6 } from '@liveauctioneers/hammer-ui-core/text';
import { TextAreaInput } from '@liveauctioneers/hammer-ui-core/textArea';
import { TextInput } from '@liveauctioneers/hammer-ui-core/textInput';
import React, { FormEvent } from 'react';
import styled from 'styled-components';

import { RatingInput } from '@/components/RatingInput/RatingInput';

const TEXT_AREA_MAX = 2000;
const HEADLINE_MAX = 100;

type Props = {
    disableSubmit: boolean;
    handleNotTalkedToHouseClick: () => void;
    handleTalkedToHouseClick: () => void;
    houseName: string;
    note: string;
    noteChange: (event: FormEvent<HTMLTextAreaElement>) => void;
    noteValid: boolean;
    ratingChangeAccuracy: (value: number) => void;
    ratingChangeOverall: (value: number) => void;
    ratingChangePayment: (value: number) => void;
    ratingChangeResponsiveness: (value: number) => void;
    ratingChangeShipping: (value: number) => void;
    receivedItems: boolean;
    review: string;
    reviewChange: (event: FormEvent<HTMLTextAreaElement>) => void;
    reviewError: boolean;
    reviewHeadlineChange: (event: FormEvent<HTMLInputElement>) => void;
    reviewHeadlineError: boolean;
    reviewSubmitted: boolean;
    reviewValid: boolean;
    submitReview: () => void;
    talkedToHouse: boolean;
};

const ReviewPageForm = (props: Props) => {
    const {
        disableSubmit,
        handleNotTalkedToHouseClick,
        handleTalkedToHouseClick,
        houseName,
        note,
        noteChange,
        noteValid,
        ratingChangeAccuracy,
        ratingChangeOverall,
        ratingChangePayment,
        ratingChangeResponsiveness,
        ratingChangeShipping,
        receivedItems,
        review,
        reviewChange,
        reviewError,
        reviewHeadlineChange,
        reviewHeadlineError,
        reviewSubmitted,
        reviewValid,
        submitReview,
        talkedToHouse,
    } = props;

    const { formatMessage, formatNumber } = useIntl();

    return (
        <div>
            <H2>
                {receivedItems && <FormattedMessage id="review_page.great" />}
                <FormattedMessage
                    id="review_page.tell_us"
                    values={{ houseName }}
                />
            </H2>
            <br />
            <BodyPrimary>
                <H2>
                    <FormattedMessage
                        id="review_page.fields.accuracy"
                        values={{ houseName }}
                    />
                </H2>
                <FormattedMessage
                    id="review_page.field_detail.accuracy"
                    values={{ houseName }}
                />
                <RatingInput onChange={ratingChangeAccuracy} />
            </BodyPrimary>
            <br />
            <BodyPrimary>
                <H2>
                    <FormattedMessage
                        id="review_page.fields.responsiveness"
                        values={{ houseName }}
                    />
                </H2>
                <div>
                    <FormattedMessage
                        id="review_page.field_detail.responsiveness_1"
                        values={{ houseName }}
                    />
                    <ResponsivenessButtonsContainer>
                        <InlineButtons
                            onClick={handleTalkedToHouseClick}
                            variant={talkedToHouse ? 'filled' : 'outlined'}
                        >
                            {formatMessage({ id: 'review_page.yes' })}
                        </InlineButtons>
                        <InlineButtons
                            onClick={handleNotTalkedToHouseClick}
                            variant={talkedToHouse ? 'outlined' : 'filled'}
                        >
                            {formatMessage({ id: 'review_page.no' })}
                        </InlineButtons>
                    </ResponsivenessButtonsContainer>
                </div>
                <br />
                {talkedToHouse && (
                    <>
                        <FormattedMessage
                            id="review_page.field_detail.responsiveness_2"
                            values={{ houseName }}
                        />
                        <RatingInput onChange={ratingChangeResponsiveness} />
                        <br />
                    </>
                )}
            </BodyPrimary>
            <BodyPrimary>
                <H2>
                    <FormattedMessage
                        id="review_page.fields.payment"
                        values={{ houseName }}
                    />
                </H2>
                <FormattedMessage
                    id="review_page.field_detail.payment"
                    values={{ houseName }}
                />
                <RatingInput onChange={ratingChangePayment} />
            </BodyPrimary>
            <br />
            <BodyPrimary>
                <H2>
                    <FormattedMessage
                        id="review_page.fields.shipping"
                        values={{ houseName }}
                    />
                </H2>
                <FormattedMessage
                    id="review_page.field_detail.shipping"
                    values={{ houseName }}
                />
                <RatingInput onChange={ratingChangeShipping} />
            </BodyPrimary>
            <br />
            <BodyPrimary>
                <H2>
                    <FormattedMessage
                        id="review_page.fields.overall"
                        values={{ houseName }}
                    />
                </H2>
                <FormattedMessage
                    id="review_page.field_detail.overall"
                    values={{ houseName }}
                />
                <RatingInput onChange={ratingChangeOverall} />
            </BodyPrimary>
            <br />
            <PublicReviewContainer>
                <BodyPrimary>
                    <H2>
                        <FormattedMessage
                            id="review_page.fields.review"
                            values={{ houseName }}
                        />
                    </H2>
                    <FormattedMessage
                        id="review_page.field_detail.review"
                        values={{ houseName }}
                    />
                    <MobileInfoBox>
                        <BodySecondary>
                            <FormattedMessage id="review_page.review_guidelines.mobileBody" />
                        </BodySecondary>
                    </MobileInfoBox>
                    <TextInput
                        aria-label={formatMessage({
                            id: 'review_page.headline_placeholder',
                        })}
                        errorText={String(
                            formatMessage(
                                { id: 'review_page.input_length_error' },
                                { limit: formatNumber(HEADLINE_MAX) }
                            )
                        )}
                        hasError={reviewHeadlineError}
                        labelText={formatMessage({
                            id: 'review_page.labels.headline',
                        })}
                        onChange={reviewHeadlineChange}
                        placeholder={formatMessage({
                            id: 'review_page.headline_placeholder',
                        })}
                        type="text"
                    />
                    <TextAreaInput
                        aria-label={formatMessage({
                            id: 'review_page.labels.public_review',
                        })}
                        errorText={String(
                            formatMessage(
                                { id: 'review_page.input_length_error' },
                                { limit: formatNumber(TEXT_AREA_MAX) }
                            )
                        )}
                        hasError={!reviewValid}
                        labelText={formatMessage({
                            id: 'review_page.labels.public_review',
                        })}
                        onChange={reviewChange}
                        placeholder={formatMessage({ id: 'review_page.public_review' }, { houseName })}
                    />
                    <H6>
                        <FormattedMessage
                            id="review_page.character_requirements"
                            values={{
                                remaining: formatNumber(
                                    TEXT_AREA_MAX - review.length > 0 ? TEXT_AREA_MAX - review.length : 0
                                ),
                            }}
                        />
                    </H6>
                </BodyPrimary>
                <br />
                <DesktopInfoBox>
                    <DesktopInfoContentContainer>
                        <BodyPrimary>
                            <H2>
                                <FormattedMessage id="review_page.review_guidelines.header" />
                            </H2>
                            <br />
                            <FormattedMessage id="review_page.review_guidelines.body" />
                            <ul>
                                <li>
                                    <FormattedMessage id="review_page.review_guidelines.lastNames" />
                                </li>
                                <li>
                                    <FormattedMessage id="review_page.review_guidelines.addresses" />
                                </li>
                                <li>
                                    <FormattedMessage id="review_page.review_guidelines.phoneNumbers" />
                                </li>
                                <li>
                                    <FormattedMessage id="review_page.review_guidelines.email" />
                                </li>
                                <li>
                                    <FormattedMessage id="review_page.review_guidelines.trackingNumbers" />
                                </li>
                                <li>
                                    <FormattedMessage id="review_page.review_guidelines.profanity" />
                                </li>
                            </ul>
                            <Link
                                href="https://help.liveauctioneers.com/article/72-review-guidelines"
                                target="_blank"
                            >
                                <FormattedMessage id="review_page.review_guidelines.link" />
                            </Link>
                        </BodyPrimary>
                    </DesktopInfoContentContainer>
                </DesktopInfoBox>
            </PublicReviewContainer>
            <br />
            <BodyPrimary>
                <H2>
                    <FormattedMessage
                        id="review_page.fields.feedback"
                        values={{ houseName }}
                    />
                </H2>
                <FormattedMessage
                    id="review_page.field_detail.feedback"
                    values={{ houseName }}
                />
                <TextAreaInput
                    aria-label={formatMessage({
                        id: 'review_page.private_feedback',
                    })}
                    errorText={String(
                        formatMessage({ id: 'review_page.input_length_error' }, { limit: formatNumber(TEXT_AREA_MAX) })
                    )}
                    hasError={!noteValid}
                    onChange={noteChange}
                    placeholder={formatMessage({
                        id: 'review_page.private_feedback',
                    })}
                />
                <H6>
                    <FormattedMessage
                        id="review_page.character_requirements"
                        values={{
                            remaining: formatNumber(TEXT_AREA_MAX - note.length > 0 ? TEXT_AREA_MAX - note.length : 0),
                        }}
                    />
                </H6>
            </BodyPrimary>
            <br />
            {reviewError && (
                <ErrorText>
                    <FormattedMessage id="review_page.error" />
                </ErrorText>
            )}
            <Button
                disabled={disableSubmit}
                loading={reviewSubmitted}
                onClick={submitReview}
            >
                {formatMessage({ id: 'submit' })}
            </Button>
        </div>
    );
};

export default ReviewPageForm;

const ErrorText = styled(H2)`
    margin: 35px auto;
    color: ${({ theme }) => theme.colors.red300};
`;

const ResponsivenessButtonsContainer = styled.div`
    display: flex;
    button {
        margin: 11px 20px 0 0;
    }
`;

const InlineButtons = styled(Button)`
    display: inline-flex;
`;

const MobileInfoBox = styled.div`
    border: solid 1px ${({ theme }) => theme.colors.blue200};
    background: ${({ theme }) => theme.colors.blueLight};
    margin: 13px 0;
    padding: 9px 13px 11px;
`;

const DesktopInfoBox = styled.div`
    align-content: flex-end;
    display: flex;
    flex-direction: row;
    border: solid 1px ${({ theme }) => theme.colors.blue200};
    background: ${({ theme }) => theme.colors.blueLight};
    max-width: 460px;
    margin-left: 20px;
`;

const DesktopInfoContentContainer = styled.div`
    margin: 12px 29px 26px;

    ul {
        list-style: none;
        margin: 22px 0 39px;
        padding-left: 0;

        span {
            padding-left: 5px;
        }

        & > ::before {
            content: '- ';
        }
    }
`;

const PublicReviewContainer = styled.div`
    display: flex;
    justify-content: space-between;
    max-width: 1089px;

    @media screen and (max-width: 760px) {
        ${DesktopInfoBox} {
            display: none;
        }
    }

    @media screen and (min-width: 761px) {
        ${MobileInfoBox} {
            display: none;
        }
    }
`;
